// pages/Meta/Connection/components/ConnectedProfilesCell.tsx
import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { UserRound, AlertTriangle, ShieldAlert } from 'lucide-react';

const MAX_STACKED_AVATARS = 3;

const AVATAR_PALETTE = [
    'bg-blue-100 text-blue-700',
    'bg-emerald-100 text-emerald-700',
    'bg-amber-100 text-amber-700',
    'bg-violet-100 text-violet-700',
    'bg-rose-100 text-rose-700',
];

function initialsFor(name: string | null): string {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase() || '?';
}

// account_action_required = checkpoint/confirmação pendente na própria
// conta (Meta não documenta um código específico para "banido"; isso é o
// mais perto disso que a Graph API expõe — ver ACCOUNT_ACTION_SUBCODES no
// job de validação). Reconectar sozinho não resolve, o dono precisa entrar
// em facebook.com primeiro.
function statusMeta(status: string | null): { label: string; severity: 'critical' | 'warning' | null } {
    if (status === 'account_action_required') {
        return { label: 'Conta com restrição — requer ação em facebook.com', severity: 'critical' };
    }
    if (status && status !== 'ok') {
        return { label: 'Token expirado — reconectar resolve', severity: 'warning' };
    }
    return { label: '', severity: null };
}

export interface ConnectedProfileEntry {
    meta_user_id: number;
    meta_user_name: string | null;
    app_name: string | null;
    token_status: string | null;
}

interface ConnectedProfilesCellProps {
    profiles: ConnectedProfileEntry[];
}

// Coluna "Perfis conectados": avatares empilhados (iniciais) + contador,
// clique abre popover com nome/app/status de cada perfil vinculado à
// página. Dados vêm já prontos do índice montado em PagesList a partir de
// UMA chamada a getUsersPagesMap — nunca faz fetch próprio (evitar N+1
// request por linha, 1 por página renderizada).
export function ConnectedProfilesCell({ profiles }: ConnectedProfilesCellProps) {
    const [open, setOpen] = useState(false);
    const [position, setPosition] = useState({ top: 0, left: 0 });
    const anchorRef = useRef<HTMLButtonElement>(null);
    const popoverRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open) return;
        const handler = (e: MouseEvent) => {
            if (
                popoverRef.current && !popoverRef.current.contains(e.target as Node) &&
                anchorRef.current && !anchorRef.current.contains(e.target as Node)
            ) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    function handleToggle(e: React.MouseEvent) {
        e.stopPropagation();
        if (!anchorRef.current) return;
        const rect = anchorRef.current.getBoundingClientRect();
        setPosition({ top: rect.bottom + 6, left: Math.max(8, rect.left - 100) });
        setOpen((o) => !o);
    }

    const total = profiles.length;
    const isSinglePoint = total === 1;
    const criticalCount = profiles.filter((p) => statusMeta(p.token_status).severity === 'critical').length;
    const warningCount = profiles.filter((p) => statusMeta(p.token_status).severity === 'warning').length;
    const hasProblem = criticalCount > 0 || warningCount > 0;
    // Contingência reduzida: se algum perfil tem problema, quantos ainda
    // restam saudáveis pra segurar a página sozinhos.
    const healthyCount = total - criticalCount - warningCount;

    return (
        <>
            <button
                ref={anchorRef}
                type="button"
                onClick={handleToggle}
                className="inline-flex items-center gap-2 rounded-lg px-1.5 py-1 hover:bg-gray-50 transition-colors"
            >
                {total === 0 ? (
                    <span className="text-xs text-gray-400 flex items-center gap-1.5">
                        <UserRound className="h-3.5 w-3.5" />
                        Nenhum
                    </span>
                ) : (
                    <>
                        <div className="flex -space-x-2">
                            {profiles.slice(0, MAX_STACKED_AVATARS).map((p, i) => {
                                const { severity } = statusMeta(p.token_status);
                                return (
                                    <div
                                        key={p.meta_user_id}
                                        className={`relative h-6 w-6 rounded-full ring-2 flex items-center justify-center text-[10px] font-semibold ${AVATAR_PALETTE[i % AVATAR_PALETTE.length]} ${
                                            severity === 'critical'
                                                ? 'ring-red-400'
                                                : severity === 'warning'
                                                ? 'ring-amber-400'
                                                : 'ring-white'
                                        }`}
                                        title={p.meta_user_name ?? `Perfil #${p.meta_user_id}`}
                                    >
                                        {initialsFor(p.meta_user_name)}
                                        {severity === 'critical' && (
                                            <ShieldAlert className="absolute -top-1.5 -right-1.5 h-3.5 w-3.5 text-red-500 bg-white rounded-full" />
                                        )}
                                        {severity === 'warning' && (
                                            <AlertTriangle className="absolute -top-1.5 -right-1.5 h-3.5 w-3.5 text-amber-500 bg-white rounded-full" />
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                        <span
                            className={`text-xs font-medium ${
                                criticalCount > 0
                                    ? 'text-red-600'
                                    : warningCount > 0 || isSinglePoint
                                    ? 'text-amber-600'
                                    : 'text-gray-600'
                            }`}
                        >
                            {total} {total === 1 ? 'perfil' : 'perfis'}
                        </span>
                    </>
                )}
            </button>

            {open && createPortal(
                <div
                    ref={popoverRef}
                    style={{ position: 'fixed', top: position.top, left: position.left, zIndex: 9999 }}
                    className="w-72 bg-white border border-gray-100 rounded-xl shadow-xl p-2"
                >
                    {criticalCount > 0 && (
                        <div className="flex items-start gap-1.5 px-2 py-1.5 mb-1 rounded-lg bg-red-50 text-red-700 text-[11px] font-medium">
                            <ShieldAlert className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                            <span>
                                {criticalCount} perfil{criticalCount > 1 ? 's' : ''} com restrição de conta —
                                contingência reduzida a {healthyCount} de {total}.
                            </span>
                        </div>
                    )}
                    {criticalCount === 0 && warningCount > 0 && (
                        <div className="flex items-start gap-1.5 px-2 py-1.5 mb-1 rounded-lg bg-amber-50 text-amber-700 text-[11px] font-medium">
                            <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                            <span>
                                {warningCount} perfil{warningCount > 1 ? 's' : ''} com token expirado — reconecte
                                em Contas.
                            </span>
                        </div>
                    )}
                    {!hasProblem && isSinglePoint && (
                        <div className="px-2 py-1.5 mb-1 rounded-lg bg-amber-50 text-amber-700 text-[11px] font-medium">
                            Apenas 1 perfil conectado — sem redundância se ele cair.
                        </div>
                    )}
                    {profiles.length === 0 ? (
                        <p className="px-2 py-2 text-xs text-gray-400">Nenhum perfil com token ativo.</p>
                    ) : (
                        <ul className="space-y-1">
                            {profiles.map((p, i) => {
                                const { label, severity } = statusMeta(p.token_status);
                                return (
                                    <li key={p.meta_user_id} className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-gray-50">
                                        <div className={`relative h-6 w-6 rounded-full flex items-center justify-center text-[10px] font-semibold shrink-0 ${AVATAR_PALETTE[i % AVATAR_PALETTE.length]}`}>
                                            {initialsFor(p.meta_user_name)}
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <p className="text-xs font-medium text-gray-800 truncate">
                                                {p.meta_user_name ?? `Perfil #${p.meta_user_id}`}
                                            </p>
                                            <p className="text-[11px] text-gray-400 truncate">
                                                {p.app_name ?? 'App desconhecido'}
                                            </p>
                                            {severity && (
                                                <p className={`text-[11px] font-medium mt-0.5 flex items-center gap-1 ${
                                                    severity === 'critical' ? 'text-red-600' : 'text-amber-600'
                                                }`}>
                                                    {severity === 'critical' ? (
                                                        <ShieldAlert className="h-3 w-3 shrink-0" />
                                                    ) : (
                                                        <AlertTriangle className="h-3 w-3 shrink-0" />
                                                    )}
                                                    {label}
                                                </p>
                                            )}
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>,
                document.body
            )}
        </>
    );
}
